import React, {
  createRef,
  MutableRefObject,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import WidgetModel, { WidgetBase } from '@mm/state/model/WidgetModel';

import { pageEntityByIdSelector } from '@mm/state/selectors/pageSelectors';
import { WidgetByTypeProps } from '../../../../editor/features/startBotPromoters/common/StartBotsDialogueEditor/WidgetByType/WidgetByType';

import css from './WidgetLayout.module.css';
import { WidgetPlatforms } from '../../../../editor/features/startBotPromoters/common/StartBotsDialogueEditor/WidgetByType/WidgetPlatforms/WidgetPlatforms';
import { PathGraphContext } from '../PathGraph/PathGraph';
import { useWidgetCreateScrollEffect } from './use-widget-create-scroll-effect';

type Props = {
  css: {
    container?: string;
    widget?: string;
  };
  children: ReactNode | Element;
  isValid?: boolean;
  isEditing?: boolean;
  invalid?: boolean;
} & Pick<
  WidgetByTypeProps,
  | 'widget'
  | 'disableDragging'
  | 'disableDeleting'
  | 'onFocus'
  | 'isFocused'
  | 'isDragging'
>;

const getWidgetErrorElementId = (id: number) => `js-widget-error-${id}`;

const getWidgetControlsElementId = (id: number) => `js-widget-controls-${id}`;

const getWidgetPathElementId = (id: number) => `js-widget-path-${id}`;

const getWidgetErrorDOMEl = (widget?: WidgetModel | WidgetBase<any>) =>
  widget?.id
    ? document.getElementById(getWidgetErrorElementId(widget?.id))
    : undefined;

const getWidgetPathDOMEl = (widget?: WidgetModel | WidgetBase<any>) =>
  widget?.id
    ? document.getElementById(getWidgetPathElementId(widget?.id))
    : undefined;

const getWidgetControlsDOMEl = (widget?: WidgetModel | WidgetBase<any>) =>
  widget?.id
    ? document.getElementById(getWidgetControlsElementId(widget?.id))
    : undefined;

type WidgetPortal = {
  widget: WidgetModel | WidgetBase<any>;
  children: ReactNode;
};

const ErrorPortal = ({ children, widget }: WidgetPortal) => {
  const target = getWidgetErrorDOMEl(widget);
  return target ? createPortal(children, target) : null;
};

const PathPortal = ({ children, widget }: WidgetPortal) => {
  const target = getWidgetPathDOMEl(widget);
  return target ? createPortal(children, target) : null;
};

const ControlsPortal = ({ children, widget }: WidgetPortal) => {
  const target = getWidgetControlsDOMEl(widget);
  return target ? createPortal(children, target) : <>{children}</>;
};

function childOf(child, parent) {
  let c: any = child;
  // eslint-disable-next-line no-cond-assign
  while ((c = c.parentNode) && c !== parent);
  return !!c;
}

const Container = React.forwardRef(
  (props: Props, ref: MutableRefObject<HTMLDivElement | null>) => {
    const {
      children,
      widget,
      isFocused,
      isDragging,
      isEditing,
      invalid,
      onFocus = () => {},
    } = props;
    const containerRef = createRef<HTMLDivElement>();
    const controlsRef = createRef<HTMLDivElement>();
    const widgetRef = createRef<HTMLDivElement>();

    const { getActiveButtonFromWidget } = useContext(PathGraphContext);

    const activeButton = getActiveButtonFromWidget?.(widget);
    const config = activeButton?.config as any;

    const dialogue = useSelector(state =>
      pageEntityByIdSelector(state, config?.pageId),
    );

    const isValid =
      widget.validated.facebook &&
      !widget.validated.buttonDialogueInValid &&
      !invalid;

    const isActive = isFocused || isDragging || isEditing;

    const cn = {
      container: classnames(css.container, props.css?.container, {
        [css.invalid]: false, // !isValid && dialogue,
      }),
      widget: classnames(css.widget, props.css?.widget, {
        [css.active]: isActive,
        [css.invalid]: !isValid,
        [css.pathed]: dialogue,
      }),
      controls: css.controls,
      platforms: css.platforms,
      footer: css.footer,
      dialogue: css.dialogue,
      errors: css.errors,
      path: css.path,
    };

    const onMouseDown = useCallback(
      event => {
        if (!widgetRef?.current || !isActive) {
          return;
        }

        const isChild = childOf(event.target, widgetRef.current);

        if (!isChild) {
          onFocus(undefined);
        }
      },
      [widgetRef, widgetRef.current, isActive],
    );

    useEffect(() => {
      if (isActive) {
        document.addEventListener('mousedown', onMouseDown);
      }
      return () => {
        document.removeEventListener('mousedown', onMouseDown);
      };
    }, [onMouseDown, isActive]);

    useWidgetCreateScrollEffect(widget?.id, widgetRef);

    return (
      <div className={cn.container} ref={containerRef}>
        <div className={cn.errors} id={getWidgetErrorElementId(widget.id)} />

        <div
          className={cn.widget}
          onClickCapture={() => onFocus(widget.id)}
          ref={widgetRef}
        >
          {children}

          <div
            className={cn.controls}
            ref={controlsRef}
            id={getWidgetControlsElementId(widget.id)}
          />

          <div className={cn.platforms}>
            <span>Valid for:</span>
            <WidgetPlatforms platforms={widget.supportedByPlatforms} />
          </div>

          {false && activeButton && dialogue && (
            <div className={cn.footer}>
              <span className={cn.dialogue}>{dialogue.name}</span>
            </div>
          )}
        </div>

        <div className={cn.path} id={getWidgetPathElementId(widget.id)} />
      </div>
    );
  },
);

export const WidgetLayout = {
  Container,
  ErrorPortal,
  PathPortal,
  ControlsPortal,
};
